import { col } from './table.js';
/**
 * The Data Hygiene view (PRD 9.3, 10.10, 12.2; TRD 11; Phase 34): the words the worklist puts
 * around the numbers Phase 16 already computes, and the arithmetic-free reshaping the card and
 * the tables both need.
 *
 * Nothing here computes a figure. `hours_at_stake`, the ranking, the counts and every metric
 * arrive from `GET /quality/hygiene` and `GET /quality/metrics` exactly as the run stored them;
 * this module decides only what each flag is *called*, what the fix is, where the fix is made,
 * and which fields of an item's evidence are worth reading. That division is the point: the
 * worklist must not be able to disagree with the forecast about which projects are inferred, and
 * the surest way to guarantee that is for the client to own no formula at all.
 *
 * The one thing it does own is the numbering. The server ranks the list (most hours at stake
 * first, TRD 11) and the client numbers the rows it was given, offset by the page — so `#1` is
 * the worst item in the whole worklist and not the worst on this screen.
 *
 * Pure: no DOM, no I/O.
 */

/**
 * Every flag of TRD 7.2, in the order the worklist groups them, with the four things the screen
 * has to say about it and the API does not carry: what it is called, what is missing in words,
 * what the fix is (the PRD 10.10 sketch's "Fix" column), and where the fix is made.
 *
 * `where` is the system that owns the record, not a link: `source_url` is the link, and it is
 * null until somebody has configured a project URL template (see the quality module's README).
 *
 * The keys of this list are held to `HYGIENE_FLAGS` by a test, so a flag added to the generators
 * cannot reach the screen as a bare `snake_case` token.
 */
export const HYGIENE_FLAG_META = Object.freeze(
  [
    {
      flag: 'missing_assignments',
      label: 'No assignments',
      missing: 'Nobody is assigned',
      fix: 'Add assignments',
      where: 'workamajig',
      why: 'The role mix comes from analogs of past work, or — with no analogs either — the project puts no demand in the forecast at all.',
    },
    {
      flag: 'missing_schedule',
      label: 'No schedule',
      missing: 'Schedule inferred',
      fix: 'Add dates to the schedule',
      where: 'workamajig',
      why: 'The run had to invent the window from a duration analog, so the hours are in plausible weeks rather than the right ones.',
    },
    {
      flag: 'missing_scope_tier',
      label: 'No amount',
      missing: 'Nothing says how big it is',
      fix: 'Set the deal amount',
      where: 'hubspot',
      why: 'The amount is what sizes a deal: without one the hours come from the median of past projects, not from what was sold.',
    },
    {
      flag: 'missing_kickoff',
      label: 'No kickoff date',
      missing: 'No kickoff date',
      fix: 'Set the kickoff date',
      where: 'hubspot',
      why: 'The window starts from the adjusted close date, so the demand can sit weeks away from where it will land.',
    },
    {
      flag: 'unmapped_taxonomy',
      label: 'Unmapped value',
      missing: 'Maps to no canonical value',
      fix: 'Map the value',
      where: 'waag',
      why: 'Records carrying it match no canonical type or service, so they are missed by analog matching and by every role row.',
    },
    {
      flag: 'unlinked_won_deal',
      label: 'Won deal, no project',
      missing: 'Won, with no project',
      fix: 'Link the deal to its project',
      where: 'waag',
      why: 'The work is sold and nothing in the forecast can see it: a won deal is not forecast, and the project it became does not exist here yet.',
    },
    {
      flag: 'outlier_weeks',
      label: 'Impossible week',
      missing: 'More hours in a week than anyone could work',
      fix: 'Spread the assignment',
      where: 'workamajig',
      why: 'One entity puts more on a person in a single week than their contracted hours allow, so every week around it reads wrong.',
    },
    {
      flag: 'stale_actuals',
      label: 'No recent time',
      missing: 'No time booked lately',
      fix: 'Book the time, or close the project',
      where: 'workamajig',
      why: 'Remaining hours, burn ratio and re-anchoring all rest on actuals; when nobody books time the evidence stops moving and the forecast keeps expecting the work.',
    },
  ].map(Object.freeze),
);

const FLAG_BY_KEY = new Map(HYGIENE_FLAG_META.map((f) => [f.flag, f]));

/** The flags, in worklist order. */
export const HYGIENE_FLAG_KEYS = Object.freeze(HYGIENE_FLAG_META.map((f) => f.flag));

/** An unknown flag still renders: its key, humanized, and no advice the screen cannot give. */
const UNKNOWN_FLAG = (flag) =>
  Object.freeze({
    flag,
    label: humanizeKey(flag),
    missing: humanizeKey(flag),
    fix: 'Fix it at the source',
    where: 'waag',
    why: '',
  });

/** @param {string} flag */
export function flagMeta(flag) {
  return FLAG_BY_KEY.get(flag) ?? UNKNOWN_FLAG(flag);
}

/** The three lifetimes of an item, and what each one means for the next run. */
export const HYGIENE_STATUS_META = Object.freeze(
  [
    { status: 'open', label: 'Open', hint: 'On the worklist.' },
    {
      status: 'resolved',
      label: 'Resolved',
      hint: 'Somebody says it is fixed. The next run confirms that by not raising it, or reopens it.',
    },
    {
      status: 'dismissed',
      label: 'Dismissed',
      hint: 'No run raises it again. Only a person undoes this.',
    },
  ].map(Object.freeze),
);

const STATUS_BY_KEY = new Map(HYGIENE_STATUS_META.map((s) => [s.status, s]));

/** @param {string} status */
export function statusMeta(status) {
  return STATUS_BY_KEY.get(status) ?? { status, label: humanizeKey(status), hint: '' };
}

/**
 * Which hours `hours_at_stake` is, in words, so the figure on the row can be checked rather than
 * believed (the phrase reads after the number: "260 h — hours the run inferred a role mix for").
 */
export const HOURS_BASIS_LABEL = Object.freeze({
  inferred_role_mix_hours: 'hours the run inferred a role mix for',
  inferred_schedule_hours: 'hours the run placed on a schedule it invented',
  pipeline_hours: "the deal's hours in this run's pipeline mode",
  carried_hours: 'hours on the records carrying this value',
  deal_amount_hours: 'the deal amount at the blended rate — demand nobody can see',
  excess_hours: 'hours above the line, summed over the weeks',
  remaining_hours: 'hours the source says are left',
});

/** @param {string} basis */
export const basisLabel = (basis) => HOURS_BASIS_LABEL[basis] ?? humanizeKey(basis);

/** What the row is about. A deal is a deal on screen; `opportunity` is the API's word. */
export const ENTITY_TYPE_LABEL = Object.freeze({
  project: 'Project',
  opportunity: 'Deal',
  taxonomy_value: 'Taxonomy value',
});

export const entityTypeLabel = (type) => ENTITY_TYPE_LABEL[type] ?? humanizeKey(type);

/** Who the owner is, as the worklist names them (`owner.source` of the API). */
export const OWNER_SOURCE_LABEL = Object.freeze({
  project_manager: 'Project manager',
  account_manager: 'Account manager',
  deal_owner: 'Deal owner',
  none: 'Nobody named',
});

/** The system the record lives in, for the sentence that says where the fix is made. */
export const SYSTEM_LABEL = Object.freeze({
  workamajig: 'Workamajig',
  hubspot: 'HubSpot',
  waag: 'this tool',
});

/**
 * The person to ask. `name` is the signed-in user the owner matched, or the name the source
 * system carries when nobody matched — the list still has to say who to ask.
 * @param {object} item
 */
export function owner(item) {
  const o = item?.owner ?? {};
  const source = o.source ?? 'none';
  return {
    name: o.name ?? o.source_name ?? null,
    user_id: o.user_id ?? null,
    email: o.email ?? null,
    source,
    source_label: OWNER_SOURCE_LABEL[source] ?? humanizeKey(source),
    /** True when the owner is a name the source carries and not a user of this tool. */
    unmatched: !o.user_id && !!(o.name ?? o.source_name),
  };
}

/**
 * The link to the record to open, or null. A deal's link is HubSpot's own; a project's comes
 * from the configured URL template and is null until somebody sets it; a taxonomy value has no
 * record anywhere to open.
 * @param {object} item
 */
export function sourceLink(item) {
  if (!item?.source_url) return null;
  const system = item.entity_type === 'opportunity' ? 'hubspot' : 'workamajig';
  return { href: item.source_url, system, label: `Open in ${SYSTEM_LABEL[system]}` };
}

/**
 * The two flags whose fix is made *here* rather than in a source system, and the review tool
 * that makes it. Both are Admin-only pages (`/utils`), so the caller decides whether to show
 * them; the mapping belongs here because it is the same "where is this fixed" question.
 * @param {object} item
 */
/** What the HubSpot sync said took the item's premise away, as a clause; null when a run or a person resolved it. */
export const RESOLVED_BY_REASON = Object.freeze({
  deal_no_longer_won: 'the deal is no longer won',
  deal_linked: 'the deal now has a project',
  deal_closed: 'the deal is closed',
  project_inactive: 'the project is inactive in Workamajig',
  project_closed: 'the project is closed',
});

/** The syncs that resolve an item on their own, by the `source` they write. */
export const RESOLVED_BY_SOURCE = Object.freeze({
  hubspot_sync: 'the HubSpot sync',
  workamajig_sync: 'the Workamajig sync',
});

/**
 * The words for a resolution a sync made rather than a run: "by the HubSpot sync: the deal is no
 * longer won". Null for anything else, so the caller says nothing rather than guessing.
 */
export function resolvedBy(item) {
  const r = item?.detail?.resolved_by;
  const source = r ? RESOLVED_BY_SOURCE[r.source] : null;
  if (!source) return null;
  const reason = RESOLVED_BY_REASON[r.reason] ?? humanizeKey(r.reason ?? '').toLowerCase();
  return reason ? `by ${source}: ${reason}` : `by ${source}`;
}

export function inToolFix(item) {
  if (item?.flag === 'unmapped_taxonomy')
    return { href: '/utils/taxonomy', label: 'Map it in the taxonomy tool' };
  if (item?.flag === 'unlinked_won_deal') {
    // Name the deal, and carry the window that raised the item. The two lookbacks are separate
    // settings and may be set apart, so a page defaulting to `linkage.lookback_days` is not on
    // its own a guarantee that a deal `quality.unlinked_won_lookback_days` reached is in the
    // list. Sending the window the item was raised under makes it one.
    const params = new URLSearchParams();
    if (item.opportunity_id) params.set('opp', item.opportunity_id);
    const days = item.detail?.lookback_days;
    if (Number.isFinite(days)) params.set('days', String(days));
    const query = params.toString();
    return {
      href: query ? `/utils/links?${query}` : '/utils/links',
      label: 'Link it in the won-deal tool',
    };
  }
  return null;
}

/**
 * Number the rows of a page. The server ranks the whole worklist, so `#1` is the worst item
 * there is and page two starts at `#26` — a rank that restarted every page would be a different
 * claim about the same list.
 * @param {object[]} items
 * @param {number} offset  the list's `offset`
 */
export function rankItems(items, offset = 0) {
  return (items ?? []).map((item, i) => ({ ...item, rank: Number(offset) + i + 1 }));
}

/**
 * One row per item for the table view and the CSV (PRD 7.3). Flat, and every field on it is a
 * field of the response or a label from this module — nothing is recomputed.
 * @param {{ items?: object[], offset?: number }} list  a `GET /quality/hygiene` response
 */
export function worklistRows(list) {
  return rankItems(list?.items ?? [], list?.offset ?? 0).map((item) => {
    const meta = flagMeta(item.flag);
    const who = owner(item);
    return {
      rank: item.rank,
      entity_type: entityTypeLabel(item.entity_type),
      entity_label: item.entity_label,
      entity_reference: item.entity_reference,
      client: item.client,
      flag: meta.label,
      missing: meta.missing,
      hours_at_stake: item.hours_at_stake,
      hours_at_stake_basis: basisLabel(item.hours_at_stake_basis),
      owner_name: who.name,
      owner_source: who.source_label,
      fix: meta.fix,
      fix_where: SYSTEM_LABEL[meta.where] ?? meta.where,
      status: statusMeta(item.status).label,
      source_url: item.source_url,
      first_seen_at: item.first_seen_at,
      last_seen_at: item.last_seen_at,
      note: item.note,
      id: item.id,
    };
  });
}

/**
 * The flag filter's chips: every flag the worklist has ever raised, with the open count and the
 * open hours behind it, in worklist order. Flags with nothing on the list at any status are left
 * out — a chip that can only ever select nothing is noise.
 * @param {object} list  a `GET /quality/hygiene` response
 */
export function flagChips(list) {
  const counts = list?.counts?.by_flag ?? [];
  const order = new Map(HYGIENE_FLAG_KEYS.map((f, i) => [f, i]));
  return counts
    .filter((c) => c.open + c.resolved + c.dismissed > 0)
    .map((c) => ({
      ...c,
      label: flagMeta(c.flag).label,
      order: order.get(c.flag) ?? 99,
    }))
    .sort((a, b) => a.order - b.order)
    .map(({ order: _order, ...chip }) => chip);
}

const num = (v) => (v === null || v === undefined || v === '' ? null : Number(v));

/**
 * The metrics of PRD 12.2 as tiles, each with the numerator and the denominator behind it, so
 * the percentage on screen can be re-derived from the run's own lists rather than trusted
 * (Phase 34's review gate asks for exactly that).
 *
 * A percentage whose denominator is zero is null in the API and stays null here: the tile says
 * so in words instead of drawing a confident 0%.
 *
 * @param {object} metrics  a `GET /quality/metrics` response
 */
export function metricTiles(metrics) {
  const p = metrics?.projects ?? {};
  const h = metrics?.hours ?? {};
  const t = metrics?.taxonomy ?? {};
  const tile = (key, label, kind, value, numerator, denominator, of, note) => ({
    key,
    label,
    kind,
    value: num(value),
    numerator: num(numerator),
    denominator: num(denominator),
    of,
    note,
  });

  return [
    tile(
      'projects_with_assignments',
      'Projects with assignments',
      'percent',
      p.pct_with_assignments,
      p.with_assignments,
      p.total,
      'projects',
      'Demand projects: every forecast project but the types not forecast and the retainers.',
    ),
    tile(
      'projects_with_schedule',
      'Projects with a schedule',
      'percent',
      p.pct_with_schedule,
      p.with_schedule,
      p.total,
      'projects',
      'A project the run did not have to invent a window for.',
    ),
    tile(
      'hours_inferred',
      'Hours inferred',
      'percent',
      h.pct_inferred,
      h.inferred,
      h.total,
      'hours',
      'Hours on rows whose schedule or role mix came from analogs.',
    ),
    tile(
      'hours_recurring_baseline',
      'Hours from the recurring baseline',
      'percent',
      h.pct_recurring_baseline,
      h.recurring_baseline,
      h.total,
      'hours',
      'Retainers and seasonal work, carried at their trailing baseline.',
    ),
    tile(
      'hours_nonproject',
      'Hours non-project',
      'percent',
      h.pct_nonproject,
      h.nonproject,
      h.total,
      'hours',
      'Internal and administrative time, where the run carries it as demand.',
    ),
    tile(
      'source_values_unmapped',
      'Source values unmapped',
      'percent',
      t.pct_unmapped,
      t.source_values_unmapped,
      t.source_values_total,
      'values',
      'Values live records still carry that map to no canonical value.',
    ),
    tile(
      'unlinked_won_deals',
      'Unlinked won deals',
      'count',
      metrics?.linkage?.unlinked_won_deals,
      metrics?.linkage?.unlinked_won_deals,
      null,
      'deals',
      'Closed-won deals inside the lookback with no project linked.',
    ),
    tile(
      'projects_burn_risk',
      'Projects at burn risk',
      'count',
      p.burn_risk,
      p.burn_risk,
      null,
      'projects',
      'Projects burning faster than their plan allows.',
    ),
  ];
}

/**
 * The metrics as table rows (PRD 7.3: every view exports). The percentage is exported as the
 * fraction the API returned, not as the rounded string on the tile.
 * @param {object} metrics
 */
export function metricRows(metrics) {
  return metricTiles(metrics).map((t) => ({
    metric: t.label,
    kind: t.kind,
    value: t.value,
    numerator: t.numerator,
    denominator: t.denominator,
    of: t.of,
  }));
}

/**
 * Unmapped source values per system and dimension — the detail behind the one percentage whose
 * fix is a list of decisions rather than one.
 * @param {object} metrics
 */
export function taxonomyRows(metrics) {
  return (metrics?.taxonomy?.by_dimension ?? []).map((d) => ({
    source_system: d.source_system,
    dimension: humanizeKey(d.dimension),
    total: d.total,
    unmapped: d.unmapped,
    mapped: Math.max(0, Number(d.total ?? 0) - Number(d.unmapped ?? 0)),
  }));
}

/**
 * What the run's own worklist snapshot said, against what the worklist says now. The metrics row
 * is immutable with the rest of the run; the worklist outlives every run and somebody may have
 * dismissed an item since. Saying so is cheaper than letting a reader find the two numbers
 * disagreeing and conclude one of them is broken.
 *
 * @param {object} metrics  a `GET /quality/metrics` response
 * @param {object} list     a `GET /quality/hygiene` response
 */
export function snapshotDrift(metrics, list) {
  const then = metrics?.hygiene ?? null;
  const now = list?.counts?.by_status ?? null;
  if (!then || !now) return null;
  const openNow = Number(now.open ?? 0);
  const openThen = Number(then.open_items ?? 0);
  return {
    open_then: openThen,
    open_now: openNow,
    hours_then: num(then.hours_at_stake),
    changed: openNow !== openThen,
  };
}

/**
 * The evidence behind one item: the fields of `detail_json` worth reading for that flag, in the
 * order a reader checks them, each with the format the value wants. The component renders them;
 * choosing them is a decision about the flag, so it lives with the flag's other words.
 *
 * Values are passed through untouched — a date stays an ISO date, hours stay a number — so the
 * formatting happens in one place (`lib/format.js`) and a test can assert on the value rather
 * than on a rendering of it.
 *
 * @param {object} item
 * @param {{ peopleById?: Map<string, string> }} [lookups]  user id → display name, for the
 *   outlier weeks, whose evidence carries ids and not names
 * @returns {Array<{ label: string, value: unknown, format: string }>}
 */
export function evidenceLines(item, { peopleById = new Map() } = {}) {
  const d = item?.detail ?? {};
  const out = [];
  const line = (label, value, format = 'text') => {
    if (value === null || value === undefined || value === '') return;
    // A date range with neither end, or an empty list, is an absence like any other.
    if (Array.isArray(value) && value.length === 0) return;
    if (format === 'date_range' && !value.from && !value.to) return;
    out.push({ label, value, format });
  };

  switch (item?.flag) {
    case 'missing_assignments':
      line('Assignments on the project', d.assignments_total, 'count');
      line('In the forecast', d.included, 'bool');
      line('Left out because', d.exclusion_reason && humanizeKey(d.exclusion_reason));
      line('Role mix from', d.role_mix_source && humanizeKey(d.role_mix_source));
      line('Analogs found', d.analog_count, 'count');
      line('Hours basis', d.hours_basis && humanizeKey(d.hours_basis));
      line('Hours allocated', d.hours_allocated, 'hours');
      line('Confidence tier', d.confidence_tier && humanizeKey(d.confidence_tier));
      break;

    case 'missing_schedule':
      line('Schedule source', d.schedule_source && humanizeKey(d.schedule_source));
      line('Window the run used', { from: d.schedule_start, to: d.schedule_end }, 'date_range');
      line('Target delivery date', d.target_delivery_date, 'date');
      line('Duration', d.duration_weekdays, 'weekdays');
      line('Duration from', d.duration_source && humanizeKey(d.duration_source));
      line('Assignments with no dates', d.assignments_inferred_schedule, 'count');
      break;

    case 'missing_scope_tier':
      line('Stage', d.stage_name);
      line('Scope tier', d.scope_tier ? humanizeKey(d.scope_tier) : 'None');
      line('Read from', d.scope_tier_source && humanizeKey(d.scope_tier_source));
      line('Deal amount', d.amount, 'money');
      line('Hours basis', d.hours_basis && humanizeKey(d.hours_basis));
      line('In the forecast', d.included, 'bool');
      line('Left out because', d.exclusion_reason && humanizeKey(d.exclusion_reason));
      line(`Hours allocated (${d.pipeline_mode ?? 'weighted'})`, d.hours_allocated, 'hours');
      break;

    case 'missing_kickoff':
      line('Stage', d.stage_name);
      line('Kickoff from', d.kickoff_source && humanizeKey(d.kickoff_source));
      line('Expected kickoff', d.expected_kickoff_date, 'date');
      line('Expected close', d.expected_close_date, 'date');
      line('Close date adjusted to', d.close_date_adjusted, 'date');
      line('Kickoff lag', d.kickoff_lag_weeks, 'weeks');
      line(`Hours allocated (${d.pipeline_mode ?? 'weighted'})`, d.hours_allocated, 'hours');
      break;

    case 'unmapped_taxonomy':
      line('Source system', d.source_system && humanizeKey(d.source_system));
      line('Dimension', d.dimension && humanizeKey(d.dimension));
      line('Source value', d.source_value);
      line('Still active in the source', d.is_active, 'bool');
      line('Records carrying it', d.carrier_count, 'count');
      line(
        'Carrying',
        (d.carriers ?? []).map(
          (c) =>
            [c.reference, c.label].filter(Boolean).join(' · ') || entityTypeLabel(c.entity_type),
        ),
        'list',
      );
      break;

    case 'unlinked_won_deal':
      line('Won on', d.closed_at, 'date');
      line('Days since', d.days_since_won, 'count');
      line('Amount', d.amount, 'money');
      line('At the blended rate', d.blended_rate, 'money');
      line('Stage', d.stage_name);
      line('Link candidates queued', d.candidate_count, 'count');
      line('Lookback', d.lookback_days, 'days');
      break;

    case 'outlier_weeks':
      line('Weeks over the line', d.week_count, 'count');
      line('The line', d.outlier_week_multiple, 'multiple');
      line('With no contracted week', d.outlier_week_hours, 'hours');
      line(
        'Weeks',
        (d.weeks ?? []).map((w) => ({
          week_start_date: w.week_start_date,
          who:
            (w.user_id && peopleById.get(w.user_id)) ||
            (w.canonical_service
              ? `${humanizeKey(w.canonical_service)} (role, nobody named)`
              : 'Somebody outside the snapshot'),
          hours: w.hours,
          line: w.line,
          excess: w.excess,
          line_basis: w.line_basis,
        })),
        'outlier_weeks',
      );
      break;

    case 'stale_actuals':
      line('Last time entry', d.last_work_date, 'date');
      line('Days since', d.days_since_last_entry, 'count');
      line('The window', d.stale_actuals_days, 'days');
      line('Hours logged', d.hours_actual, 'hours');
      line('Hours planned', d.hours_planned, 'hours');
      line('Burn ratio', d.burn_ratio, 'percent');
      line('At burn risk', d.burn_risk, 'bool');
      break;

    default:
      for (const [key, value] of Object.entries(d)) {
        if (value === null || value === undefined || typeof value === 'object') continue;
        line(humanizeKey(key), value);
      }
  }
  return out;
}

/** `missing_scope` → `Missing scope`. Sentence case, because these are values and not titles. */
export function humanizeKey(value) {
  if (value === null || value === undefined || value === '') return '';
  const words = String(value).replace(/[_-]+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

// ---------------------------------------------------------------------------------------------
// Table columns (Phase 37)
//
// A specification, not a rendering: the screen decorates it with `withFormats` and the CSV
// writer reads the same list, so an export cannot hold a different set of columns from the
// table it came from.

/** The worklist, ranked by the hours whose confidence a fix would improve. */
export function hygieneColumns() {
  return [
    col.count('rank', '#'),
    col.text('entity_type', 'What'),
    col.text('entity_label', 'Name'),
    col.text('entity_reference', 'Reference'),
    col.text('client', 'Client'),
    col.text('flag', 'Problem'),
    col.text('missing', 'Missing'),
    col.hours('hours_at_stake', 'Hours at stake'),
    col.text('hours_at_stake_basis', 'Which hours'),
    col.text('owner_name', 'Owner'),
    col.text('owner_source', 'Owner is'),
    col.text('fix', 'Fix'),
    col.text('fix_where', 'Fix in'),
    col.text('status', 'Status'),
    col.text('source_url', 'Record'),
    col.date('first_seen_at', 'First seen'),
    col.date('last_seen_at', 'Last raised'),
    col.text('note', 'Note'),
  ];
}

/** The run's completeness and inference ratios, each with the numbers behind it. */
export function qualityMetricColumns() {
  return [
    col.text('metric', 'Metric'),
    col.number('value', 'Value'),
    col.count('numerator', 'Numerator'),
    col.count('denominator', 'Denominator'),
    col.text('of', 'Counted in'),
  ];
}

/** One row per source system and dimension: how much of the taxonomy is mapped. */
export function qualityTaxonomyColumns() {
  return [
    col.text('source_system', 'Source system'),
    col.text('dimension', 'Dimension'),
    col.count('total', 'Values carried'),
    col.count('mapped', 'Mapped'),
    col.count('unmapped', 'Unmapped'),
  ];
}
